import { useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DossierTable, NumberedSection, PanelCard, RegaraNoteList } from './DossierPrimitives';
import {
  fetchUdiEudamedData,
  type UdiEudamedData,
  type UdiAttribute,
} from '@/features/dossier/api';

/**
 * EU MDR UDI / EUDAMED Data panel (Regulation (EU) 2017/745 — Annex VI).
 *
 * Split into:
 *   - `UdiEudamedDataRenderer` — presentational, accepts a
 *     `UdiEudamedData` as a prop. Unit-testable without fetch.
 *   - `UdiEudamedDataPanel` — container; fetches via
 *     `fetchUdiEudamedData` and delegates to the renderer.
 *
 * §1  Device Identifiers
 *     Basic UDI-DI, UDI-DI, Device name, Model / reference,
 *     Manufacturer SRN, EMDN code, Risk class
 * §2  Special Device Characteristics
 *     Each `special_attributes` entry as name: value-or-placeholder
 * Notes (when present)
 * Download PDF / DOCX links targeting
 *     GET …/udi-eudamed-data?device_class=…&format=pdf|docx
 *
 * Types are single-sourced in `@/features/dossier/api` — NOT re-declared here.
 */

// Re-export types so existing importers (e.g. tests) keep resolving.
export type { UdiEudamedData, UdiAttribute };

// ── Null-identifier placeholder ───────────────────────────────────────────────

const PENDING = '[To be completed by the manufacturer]';

// RA acceptance checklist B.2: before the UDIs are assigned, a UDI assignment plan naming the issuing
// entity satisfies the item. LOCKSTEP with backend eu_udi_renderer (BASIC_UDI_HINT / UDI_DI_HINT).
const BASIC_UDI_HINT = 'Basic UDI-DI assigned through an issuing entity, then registered in EUDAMED';
const UDI_DI_HINT =
  'UDI assignment plan: the issuing entity (GS1, HIBCC, ICCBBA or IFA) and the UDI-DI for each ' +
  'configuration and packaging level';
// LOCKSTEP with backend eu_device_render_shared.ISSUING_ENTITY_HINT.
const ISSUING_ENTITY_HINT = 'Issuing entity: GS1, HIBCC, ICCBBA or IFA';
// RA B.4 (device side). LOCKSTEP with eu_device_render_shared.EUDAMED_REGISTRATION_HINT.
const EUDAMED_REGISTRATION_HINT = "State the device's EUDAMED registration status (UDI / Device module)";

function NullField({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | null | undefined;
  hint?: string;
}) {
  if (value) {
    return (
      <div className="grid grid-cols-[minmax(180px,220px)_1fr] gap-x-4 gap-y-0.5 my-2">
        <div className="font-semibold text-ink/80 text-[13px]">{label}</div>
        <div className="text-[13.5px] min-w-0">{value}</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-[minmax(180px,220px)_1fr] gap-x-4 gap-y-0.5 my-2">
      <div className="font-semibold text-ink/80 text-[13px]">{label}</div>
      <div className="text-[13.5px] min-w-0">
        <span className="text-muted italic">
          {hint ? `${PENDING.slice(0, -1)}: ${hint}]` : PENDING}
        </span>
      </div>
    </div>
  );
}

// ── Presentational renderer ───────────────────────────────────────────────────

export function UdiEudamedDataRenderer({
  result,
  productId,
  deviceClass,
  hideDownload = false,
  regulation = 'MDR',
}: {
  result: UdiEudamedData;
  productId: number;
  deviceClass: string;
  hideDownload?: boolean;
  /** Cover framing only — 'MDR' (default, byte-identical) or 'IVDR' (Reg. 2017/746). */
  regulation?: 'MDR' | 'IVDR';
}) {
  const baseUrl = `/api/products/${productId}/udi-eudamed-data?device_class=${encodeURIComponent(deviceClass)}`;

  return (
    <div className="space-y-4">
      <DocCoverBlock deviceClass={result.device_class} regulation={regulation} />

      <Section1DeviceIdentifiers result={result} />
      <Section2SpecialCharacteristics attributes={result.special_attributes} />

      <RegaraNoteList notes={result.notes} />

      {!hideDownload && <DownloadBar baseUrl={baseUrl} />}
    </div>
  );
}

// ── Cover block ───────────────────────────────────────────────────────────────

function DocCoverBlock({
  deviceClass,
  regulation,
}: {
  deviceClass: string;
  regulation: 'MDR' | 'IVDR';
}) {
  const regNum = regulation === 'IVDR' ? '2017/746' : '2017/745';
  return (
    <PanelCard>
      <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-1.5">
        EU {regulation} UDI / EUDAMED Data
      </div>
      <p className="text-[11px] text-muted m-0 mb-4">
        Regulation (EU) {regNum} — Annex VI
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-[#f8f9fa] rounded-md py-2.5 px-3">
          <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted mb-0.5">
            Device class (EU {regulation})
          </div>
          <div className="text-[13px] text-navy font-semibold">{deviceClass}</div>
        </div>
      </div>
    </PanelCard>
  );
}

// ── §1 Device Identifiers ─────────────────────────────────────────────────────

function Section1DeviceIdentifiers({ result }: { result: UdiEudamedData }) {
  const rows = (result.udi_dis ?? []).filter((r) => r.udi_di);
  // Several device models: each UDI-DI names its Basic UDI-DI (LOCKSTEP with eu_device_render_shared).
  const several = (result.basic_udi_dis ?? []).length > 1;
  return (
    <NumberedSection prefix="Section" num={1} title="Device Identifiers">
      <NullField label="Basic UDI-DI:" value={result.basic_udi_di} hint={BASIC_UDI_HINT} />
      <NullField label="Issuing entity:" value={result.issuing_entity} hint={ISSUING_ENTITY_HINT} />
      <NullField
        label="EUDAMED device registration:"
        value={result.eudamed_registration}
        hint={EUDAMED_REGISTRATION_HINT}
      />
      {rows.length > 0 ? (
        <div className="my-2">
          {/* RA B.3: "Updated list of all assigned UDIs kept as part of the TD" (Art. 27(7)). */}
          <div className="font-semibold text-ink/80 text-[13px]">
            Assigned UDI-DIs (the list kept up to date in the technical documentation):
          </div>
          <DossierTable
            headers={[...(several ? ['Basic UDI-DI'] : []), 'UDI-DI', 'Configuration / model', 'Packaging level']}
            rows={rows.map((r) => [
              ...(several ? [r.basic_udi_di || '—'] : []),
              r.udi_di,
              r.configuration || '—',
              r.packaging_level || '—',
            ])}
          />
        </div>
      ) : (
        <NullField label="UDI-DI:" value={result.udi_di} hint={UDI_DI_HINT} />
      )}
      <NullField label="Device name:" value={result.device_name} />
      <NullField label="Model / reference:" value={result.model_reference} />
      <NullField label="Manufacturer SRN:" value={result.manufacturer_srn} />
      <NullField label="EMDN code:" value={result.emdn_code} />
      {/* Risk class is always present — rendered as a plain value row */}
      <div className="grid grid-cols-[minmax(180px,220px)_1fr] gap-x-4 gap-y-0.5 my-2">
        <div className="font-semibold text-ink/80 text-[13px]">Risk class:</div>
        <div className="text-[13.5px] min-w-0">{result.risk_class}</div>
      </div>
    </NumberedSection>
  );
}

// ── §2 Special Device Characteristics ────────────────────────────────────────

function Section2SpecialCharacteristics({ attributes }: { attributes: UdiAttribute[] }) {
  return (
    <NumberedSection prefix="Section" num={2} title="Special Device Characteristics (Annex VI Part B)">
      {attributes.length > 0 ? (
        <div className="space-y-0">
          {attributes.map((attr, i) => (
            <div
              key={i}
              className="grid grid-cols-[minmax(180px,220px)_1fr] gap-x-4 gap-y-0.5 my-2"
            >
              <div className="font-semibold text-ink/80 text-[13px]">{attr.name}</div>
              <div className="text-[13.5px] min-w-0">
                {attr.value != null ? (
                  attr.value
                ) : (
                  <span className="text-muted italic">{PENDING}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[13.5px] text-muted italic m-0">{PENDING}</p>
      )}
    </NumberedSection>
  );
}

// ── Download bar ──────────────────────────────────────────────────────────────

function DownloadBar({ baseUrl }: { baseUrl: string }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button variant="brand" size="sm" asChild>
        <a
          href={`${baseUrl}&format=pdf`}
          target="_blank"
          rel="noopener"
          title="Download UDI / EUDAMED Data as PDF"
        >
          <Download className="size-3.5" /> Download PDF
        </a>
      </Button>
    </div>
  );
}

// ── Container (fetches data) ──────────────────────────────────────────────────

type FetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; result: UdiEudamedData };

export function UdiEudamedDataPanel({
  productId,
  deviceClass,
}: {
  productId: number;
  deviceClass: string;
}) {
  const [state, setState] = useState<FetchState>({ status: 'idle' });

  const load = async () => {
    setState({ status: 'loading' });
    try {
      const result = await fetchUdiEudamedData(productId, deviceClass);
      setState({ status: 'ok', result });
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Failed to load UDI / EUDAMED data.',
      });
    }
  };

  if (state.status === 'idle') {
    return (
      <PanelCard>
        <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-3">
          EU MDR UDI / EUDAMED Data
        </div>
        <p className="text-[13px] text-muted m-0 mb-4">
          Generate UDI / EUDAMED data summary for this device class.
        </p>
        <Button variant="brand" size="sm" onClick={() => void load()}>
          Load UDI / EUDAMED Data
        </Button>
      </PanelCard>
    );
  }

  if (state.status === 'loading') {
    return (
      <PanelCard>
        <p className="text-[13px] text-muted italic m-0">
          Loading UDI / EUDAMED data…
        </p>
      </PanelCard>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6">
        <div className="font-semibold text-state-error-text text-[14px] mb-1">
          Could not load UDI / EUDAMED data
        </div>
        <p className="text-[13px] text-state-error-text m-0 mb-3">{state.message}</p>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <UdiEudamedDataRenderer
      result={state.result}
      productId={productId}
      deviceClass={deviceClass}
    />
  );
}
